import React, { useEffect, useState } from 'react';
import { Loader2, MapPin, Cloud, CloudRain, CloudSnow, Sun, CloudSun, CloudLightning, CloudFog, Wind } from 'lucide-react';

// City coordinates for Lake Garda destinations — legacy fallback used only
// when the city document has no `latitude`/`longitude` filled in.
const CITY_COORDS = {
  desenzano: { lat: 45.4694, lng: 10.5352, tz: 'Europe/Rome' },
  sirmione: { lat: 45.4907, lng: 10.6062, tz: 'Europe/Rome' },
  peschiera: { lat: 45.4408, lng: 10.6931, tz: 'Europe/Rome' }
};

// WMO weather codes → { icon, label(it/en/de/fr/es) }
const CODE_MAP = {
  0:  { Icon: Sun,          label: { it: 'Sereno', en: 'Clear', de: 'Klar', fr: 'Dégagé', es: 'Despejado' } },
  1:  { Icon: Sun,          label: { it: 'Prev. sereno', en: 'Mostly clear', de: 'Meist klar', fr: 'Peu nuageux', es: 'Casi despejado' } },
  2:  { Icon: CloudSun,     label: { it: 'Parz. nuvoloso', en: 'Partly cloudy', de: 'Teils bewölkt', fr: 'Partiellement nuageux', es: 'Parcialmente nublado' } },
  3:  { Icon: Cloud,        label: { it: 'Nuvoloso', en: 'Overcast', de: 'Bewölkt', fr: 'Couvert', es: 'Nublado' } },
  45: { Icon: CloudFog,     label: { it: 'Nebbia', en: 'Fog', de: 'Nebel', fr: 'Brouillard', es: 'Niebla' } },
  48: { Icon: CloudFog,     label: { it: 'Nebbia', en: 'Fog', de: 'Nebel', fr: 'Brouillard', es: 'Niebla' } },
  51: { Icon: CloudRain,    label: { it: 'Pioviggine', en: 'Drizzle', de: 'Nieselregen', fr: 'Bruine', es: 'Llovizna' } },
  53: { Icon: CloudRain,    label: { it: 'Pioviggine', en: 'Drizzle', de: 'Nieselregen', fr: 'Bruine', es: 'Llovizna' } },
  55: { Icon: CloudRain,    label: { it: 'Pioviggine', en: 'Drizzle', de: 'Nieselregen', fr: 'Bruine', es: 'Llovizna' } },
  61: { Icon: CloudRain,    label: { it: 'Pioggia lieve', en: 'Light rain', de: 'Leichter Regen', fr: 'Pluie faible', es: 'Lluvia ligera' } },
  63: { Icon: CloudRain,    label: { it: 'Pioggia', en: 'Rain', de: 'Regen', fr: 'Pluie', es: 'Lluvia' } },
  65: { Icon: CloudRain,    label: { it: 'Pioggia forte', en: 'Heavy rain', de: 'Starker Regen', fr: 'Forte pluie', es: 'Lluvia fuerte' } },
  71: { Icon: CloudSnow,    label: { it: 'Neve leggera', en: 'Light snow', de: 'Leichter Schnee', fr: 'Neige faible', es: 'Nieve ligera' } },
  73: { Icon: CloudSnow,    label: { it: 'Neve', en: 'Snow', de: 'Schnee', fr: 'Neige', es: 'Nieve' } },
  75: { Icon: CloudSnow,    label: { it: 'Neve forte', en: 'Heavy snow', de: 'Starker Schnee', fr: 'Forte neige', es: 'Nieve fuerte' } },
  77: { Icon: CloudSnow,    label: { it: 'Grandine', en: 'Snow grains', de: 'Schneegriesel', fr: 'Neige en grains', es: 'Cinarra' } },
  80: { Icon: CloudRain,    label: { it: 'Rovesci', en: 'Showers', de: 'Schauer', fr: 'Averses', es: 'Chubascos' } },
  81: { Icon: CloudRain,    label: { it: 'Rovesci', en: 'Showers', de: 'Schauer', fr: 'Averses', es: 'Chubascos' } },
  82: { Icon: CloudRain,    label: { it: 'Rovesci forti', en: 'Heavy showers', de: 'Starke Schauer', fr: 'Averses fortes', es: 'Chubascos fuertes' } },
  95: { Icon: CloudLightning, label: { it: 'Temporale', en: 'Thunderstorm', de: 'Gewitter', fr: 'Orage', es: 'Tormenta' } },
  96: { Icon: CloudLightning, label: { it: 'Temporale', en: 'Thunderstorm', de: 'Gewitter', fr: 'Orage', es: 'Tormenta' } },
  99: { Icon: CloudLightning, label: { it: 'Temporale', en: 'Thunderstorm', de: 'Gewitter', fr: 'Orage', es: 'Tormenta' } }
};

const codeInfo = (code) => CODE_MAP[code] || { Icon: Cloud, label: { it: '—', en: '—', de: '—', fr: '—', es: '—' } };

const DAY_LABELS = {
  it: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  fr: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
  es: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
};

const TITLES = {
  it: 'Meteo',
  en: 'Weather',
  de: 'Wetter',
  fr: 'Météo',
  es: 'Clima'
};

const NOW_LABEL = {
  it: 'Adesso',
  en: 'Now',
  de: 'Jetzt',
  fr: 'Maintenant',
  es: 'Ahora'
};

const ERR_LABEL = {
  it: 'Meteo non disponibile',
  en: 'Weather unavailable',
  de: 'Wetter nicht verfügbar',
  fr: 'Météo indisponible',
  es: 'Clima no disponible'
};

const POWERED_BY = 'Open-Meteo';

export default function WeatherWidget({ citySlug, cityName, lang = 'it', layout = 'compact', latitude, longitude, timezone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Prefer the explicit lat/lng passed in (from the DB city doc) — this way
  // every newly-created city gets weather automatically without touching this file.
  const coords = (latitude != null && longitude != null)
    ? { lat: latitude, lng: longitude, tz: timezone || 'Europe/Rome' }
    : CITY_COORDS[citySlug];

  useEffect(() => {
    if (!coords) { setErr('no-coords'); setLoading(false); return; }
    let alive = true;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}` +
      `&current=temperature_2m,weather_code,wind_speed_10m,apparent_temperature` +
      `&daily=temperature_2m_max,temperature_2m_min,weather_code` +
      `&timezone=${encodeURIComponent(coords.tz)}&forecast_days=5`;
    setLoading(true);
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(json => { if (alive) { setData(json); setErr(null); } })
      .catch(e => { if (alive) setErr(String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [citySlug, coords?.lat, coords?.lng]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md px-6 py-5 flex items-center gap-3" data-testid="weather-loading">
        <Loader2 size={18} className="animate-spin text-[color:var(--yup-primary)]" />
        <span className="text-white/70 text-sm">Loading meteo…</span>
      </div>
    );
  }

  if (err || !data?.current) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md px-6 py-5 text-white/60 text-sm" data-testid="weather-error">
        {ERR_LABEL[lang] || ERR_LABEL.en}
      </div>
    );
  }

  const cur = data.current;
  const daily = data.daily || {};
  const curCode = codeInfo(cur.weather_code);
  const CurIcon = curCode.Icon;
  const dayLabels = DAY_LABELS[lang] || DAY_LABELS.en;

  const days = (daily.time || []).map((iso, i) => {
    const d = new Date(iso + 'T00:00:00');
    const info = codeInfo(daily.weather_code?.[i]);
    return {
      iso,
      dayIdx: d.getDay(),
      max: Math.round(daily.temperature_2m_max?.[i]),
      min: Math.round(daily.temperature_2m_min?.[i]),
      Icon: info.Icon,
      label: info.label[lang] || info.label.en
    };
  });

  return (
    <div
      className="rounded-2xl border border-[color:var(--yup-primary)]/25 bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-transparent backdrop-blur-xl shadow-2xl overflow-hidden"
      data-testid="weather-widget"
    >
      {layout === 'wide' ? (
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          {/* LEFT: current condition, larger */}
          <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/5 flex items-center gap-5 md:gap-6">
            <div
              className="w-20 h-20 md:w-24 md:h-24 rounded-3xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(188,172,138,0.25), rgba(188,172,138,0.05))', border: '1px solid rgba(188,172,138,0.35)' }}
            >
              <CurIcon size={52} className="text-[color:var(--yup-primary)]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--yup-primary)]">
                <MapPin size={12} />
                <span className="truncate">{cityName} · {TITLES[lang] || TITLES.en}</span>
              </div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="text-6xl md:text-7xl font-bold text-white leading-none" data-testid="weather-current-temp">
                  {Math.round(cur.temperature_2m)}°
                </span>
              </div>
              <div className="mt-2 text-white/85 text-base md:text-lg">
                {curCode.label[lang] || curCode.label.en}
              </div>
              <div className="mt-3 flex items-center gap-5 text-sm text-white/60">
                <span>{NOW_LABEL[lang] || NOW_LABEL.en}</span>
                <span className="inline-flex items-center gap-1.5"><Wind size={14} /> {Math.round(cur.wind_speed_10m)} km/h</span>
              </div>
            </div>
          </div>

          {/* RIGHT: 5-day forecast */}
          <div className="p-5 md:p-6 grid grid-cols-5 gap-2" data-testid="weather-forecast">
            {days.slice(0, 5).map((d, i) => {
              const Icon = d.Icon;
              return (
                <div
                  key={d.iso}
                  className={`flex flex-col items-center justify-center gap-2 py-4 rounded-xl text-center transition ${i === 0 ? 'bg-[color:var(--yup-primary)]/10 border border-[color:var(--yup-primary)]/30' : 'hover:bg-white/5'}`}
                  data-testid={`weather-day-${i}`}
                >
                  <div className="text-[10px] tracking-widest uppercase text-white/60">
                    {i === 0 ? (NOW_LABEL[lang] || NOW_LABEL.en) : dayLabels[d.dayIdx]}
                  </div>
                  <Icon size={30} className="text-[color:var(--yup-primary)]/95" />
                  <div className="text-sm text-white/90">
                    <span className="font-semibold">{d.max}°</span>
                    <span className="text-white/45"> / {d.min}°</span>
                  </div>
                  <div className="text-[10px] text-white/50 leading-tight">{d.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {/* Compact — original layout */}
          <div className="px-6 py-6 md:px-8 md:py-7 flex items-center justify-between gap-4 border-b border-white/5">
            <div className="flex items-center gap-4 min-w-0">
              <div
                className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(188,172,138,0.25), rgba(188,172,138,0.05))', border: '1px solid rgba(188,172,138,0.35)' }}
              >
                <CurIcon size={32} className="text-[color:var(--yup-primary)]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--yup-primary)]">
                  <MapPin size={12} />
                  <span className="truncate">{cityName} · {TITLES[lang] || TITLES.en}</span>
                </div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span className="text-4xl md:text-5xl font-bold text-white leading-none" data-testid="weather-current-temp">
                    {Math.round(cur.temperature_2m)}°
                  </span>
                  <span className="text-white/70 text-sm">{curCode.label[lang] || curCode.label.en}</span>
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-white/60">
                  <span>{NOW_LABEL[lang] || NOW_LABEL.en}</span>
                  <span className="inline-flex items-center gap-1"><Wind size={12} /> {Math.round(cur.wind_speed_10m)} km/h</span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-3 py-4 md:px-4 grid grid-cols-5 gap-1" data-testid="weather-forecast">
            {days.slice(0, 5).map((d, i) => {
              const Icon = d.Icon;
              return (
                <div
                  key={d.iso}
                  className={`flex flex-col items-center gap-1.5 py-2 rounded-xl text-center ${i === 0 ? 'bg-white/5' : ''}`}
                  data-testid={`weather-day-${i}`}
                >
                  <div className="text-[10px] tracking-widest uppercase text-white/60">
                    {i === 0 ? (NOW_LABEL[lang] || NOW_LABEL.en) : dayLabels[d.dayIdx]}
                  </div>
                  <Icon size={20} className="text-[color:var(--yup-primary)]/90" />
                  <div className="text-xs text-white/85">
                    <span className="font-semibold">{d.max}°</span>
                    <span className="text-white/45"> / {d.min}°</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="px-4 pb-3 text-[10px] text-white/35 text-right tracking-wider">
        {POWERED_BY}
      </div>
    </div>
  );
}
